/**
 * Environment configuration — web implementation.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so these must be provided
 * as build-time variables (.env file locally, CI variables in the pipeline).
 */
type ViteEnv = Record<string, string | boolean | undefined>;

// Read through a cast so this module type-checks without pulling in `vite/client`.
const env: ViteEnv = (import.meta as unknown as { env?: ViteEnv }).env ?? {};

const str = (key: string): string =>
  typeof env[key] === 'string' ? (env[key] as string) : '';

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

export const APP_ENV = str('VITE_APP_ENV') || 'development';
export const GA4_MEASUREMENT_ID = str('VITE_GA4_MEASUREMENT_ID');

// Empty base URL means same-origin requests. The Vite dev server proxies
// `/api` to the real API host (see apps/web/vite.config.ts) because the API
// does not allow cross-origin (CORS) requests from browsers.
const rawApiBaseUrl = str('VITE_API_BASE_URL');

export const API_BASE_URL = rawApiBaseUrl
  ? stripTrailingSlash(rawApiBaseUrl)
  : '';

export const IMAGE_BASE_URL = stripTrailingSlash(str('VITE_IMAGE_BASE_URL'));

export const GIPHY_API_KEY = str('VITE_GIPHY_API_KEY');

export const SHARE_POST_URL = stripTrailingSlash(str('VITE_SHARE_POST_URL'));

const rawShareProfileUrl = str('VITE_SHARE_PROFILE_URL');
export const SHARE_PROFILE_URL = stripTrailingSlash(
  rawShareProfileUrl && !rawShareProfileUrl.endsWith('/u')
    ? rawShareProfileUrl
    : SHARE_POST_URL.replace(/\/post\/?$/i, '/profile'),
);

/** Base URL for shared group deep links (no trailing slash), e.g. https://demo.weightlossbuddy.com/group. */
const rawShareGroupUrl = str('VITE_SHARE_GROUP_URL');
export const SHARE_GROUP_URL = stripTrailingSlash(
  rawShareGroupUrl
    ? rawShareGroupUrl
    : SHARE_POST_URL.replace(/\/post\/?$/i, '/group'),
);

export const SKINNY_NEWS_URL = str('VITE_SKINNY_NEWS_URL');

// ─── Social sign-in (web OAuth) ──────────────────────────────────────────────
// Browser OAuth client IDs. Empty => that provider's button is hidden on web
// (see platform/socialAuth.web.ts). These are distinct from the mobile app's
// native credentials and must be configured in each provider's console with
// the web origin (Google/Apple) / site URL (Facebook) authorised.
export const GOOGLE_CLIENT_ID = str('VITE_GOOGLE_CLIENT_ID');
export const GOOGLE_WEB_CLIENT_ID = str('VITE_GOOGLE_WEB_CLIENT_ID') || GOOGLE_CLIENT_ID;
export const GOOGLE_IOS_CLIENT_ID = str('VITE_GOOGLE_IOS_CLIENT_ID');
export const FACEBOOK_APP_ID = str('VITE_FACEBOOK_APP_ID');
export const APPLE_CLIENT_ID = str('VITE_APPLE_CLIENT_ID');
export const APPLE_REDIRECT_URI = str('VITE_APPLE_REDIRECT_URI');

export const buildSharePostUrl = (postId: string | number): string => {
  const base = SHARE_POST_URL || (
    typeof window === 'undefined'
      ? '/post'
      : `${window.location.origin}/post`
  );
  return `${base}/${encodeURIComponent(String(postId).trim())}`;
};

export const buildShareProfileUrl = (userId: string | number): string =>
  `${SHARE_PROFILE_URL}/${userId}`;

export const buildShareGroupUrl = (groupId: string | number): string =>
  `${SHARE_GROUP_URL}/${groupId}`;

export const IS_DEV = env.DEV === true;

if (IS_DEV) {
  if (!GIPHY_API_KEY) {
    console.warn('VITE_GIPHY_API_KEY missing from .env');
  }
  if (!IMAGE_BASE_URL) {
    console.warn('VITE_IMAGE_BASE_URL missing from .env');
  }
  if (!GOOGLE_CLIENT_ID) {
    console.warn('VITE_GOOGLE_CLIENT_ID missing — Google sign-in hidden on web');
  }
  if (!FACEBOOK_APP_ID) {
    console.warn('VITE_FACEBOOK_APP_ID missing — Facebook sign-in hidden on web');
  }
  if (!APPLE_CLIENT_ID) {
    console.warn('VITE_APPLE_CLIENT_ID missing — Apple sign-in hidden on web');
  }
}
